// La portada de www.brujel.com y el formulario de entrada, en la misma
// pantalla. Un solo componente con tres modos —entrar, registrarse,
// recuperar— porque comparten el formulario y separarlos duplicaría
// todo. El argumento de venta vive en `Portada.js`.
//
// ═══════════════════════════════════════════════════════════════════
// POR QUÉ ARRANCA EN "CREAR CUENTA" Y NO EN "ENTRAR" (2026-09-12)
// ═══════════════════════════════════════════════════════════════════
//
// Hasta hoy abría en modo entrar, que era lo correcto cuando esto era
// solo un login: los dos usuarios que había ya tenían cuenta.
//
// Ahora la misma pantalla es la portada del sitio. El que llega por un
// enlace no tiene cuenta, y cada paso entre leer el argumento y poder
// escribir su email pierde gente. El que ya tiene cuenta hace un clic
// de más y el navegador le autocompleta el resto.
//
// ⚠️  ESTO MOLESTA PRIMERO AL DUEÑO. Mientras el tráfico sea él
//     mismo, el 100% de las visitas van a querer el modo que NO es el
//     predeterminado. Está puesto así a propósito, para el tráfico que
//     todavía no llegó.

import { useState } from "react";
import { useAuth } from "./AuthContext";
import * as api from "./api";
import { idiomaActual, t, tError, useIdioma } from "./i18n";
import {
  PortadaArgumento,
  PortadaBarra,
  PortadaPie,
  PortadaPruebas,
} from "./Portada";

export default function Login() {
  // Re-renderiza si cambia el idioma.
  useIdioma();
  const { entrar, registrarse } = useAuth();

  // "entrar" | "registrarse" | "olvide"
  const [modo, setModo] = useState("registrarse");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [nombre, setNombre] = useState("");
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [ocupado, setOcupado] = useState(false);

  const esRegistro = modo === "registrarse";
  const esOlvide = modo === "olvide";

  function cambiarModo(nuevo) {
    setModo(nuevo);
    setError("");
    setAviso("");
  }

  async function enviar(e) {
    e.preventDefault();
    setError("");
    setAviso("");

    if (!email.trim()) {
      setError(t("login.faltaEmail", "Escribe tu email"));
      return;
    }

    if (!esOlvide && !password) {
      setError(t("login.faltaPassword", "Escribe tu contraseña"));
      return;
    }

    if (esRegistro && password.length < 8) {
      setError(t("login.passwordCorta", "La contraseña tiene que tener al menos 8 caracteres"));
      return;
    }

    setOcupado(true);

    try {
      if (esOlvide) {
        await api.pedirReset(email.trim());
        // Mensaje deliberadamente ambiguo: no confirma si el email
        // existe o no. Si lo confirmara, serviría para averiguar qué
        // direcciones tienen cuenta.
        setAviso(
          t(
            "login.avisoReset",
            "Si hay una cuenta con {email}, te va a llegar un correo con el link para cambiar la contraseña.",
            { email: email.trim() }
          )
        );
        setModo("entrar");
        setPassword("");
      } else if (esRegistro) {
        /* ⚠️  EL IDIOMA VIAJA CON EL REGISTRO, Y SIN ESTO LA FUNCIÓN
         *     SE ROMPE JUSTO EN EL MOMENTO QUE IMPORTA.
         *
         * `perfiles.idioma` nace con el default de la base ('es'). Sin
         * mandarlo, alguien elegía francés, se registraba, entraba —y
         * la app le contestaba en español, porque al entrar manda el
         * perfil. El idioma elegido se perdía exactamente en el paso
         * donde la persona se compromete.
         *
         * Con la portada esto importa todavía más: el selector de
         * idioma es ahora lo primero que toca un visitante que no lee
         * inglés, y lo que elige ahí tiene que seguir puesto adentro
         * de la aplicación. Lo cuida `test_alta.js`. */
        const data = await registrarse(email.trim(), password, nombre.trim(), idiomaActual());

        if (data.necesitaConfirmar) {
          setAviso(
            t("login.avisoConfirmar", "Te mandamos un correo a {email}. Confirma tu cuenta y después entra.", {
              email: email.trim(),
            })
          );
          setModo("entrar");
          setPassword("");
        }
      } else {
        await entrar(email.trim(), password);
      }
    } catch (err) {
      setError(tError(err));
    } finally {
      setOcupado(false);
    }
  }

  const titulo = esOlvide
    ? t("login.recuperar", "Recuperar contraseña")
    : esRegistro
    ? t("login.crearCuenta", "Crea tu cuenta")
    : t("login.entrarTitulo", "Entra a tu armario");

  const textoBoton = ocupado
    ? t("comun.unMomento", "Un momento...")
    : esOlvide
    ? t("login.mandarLink", "Mandarme el link")
    : esRegistro
    ? t("login.crearBoton", "Crear cuenta")
    : t("login.entrar", "Entrar");

  /* El texto del botón que cambia de modo. Son tres textos y no dos
   * porque ahora el modo de partida es el registro: "Volver a entrar"
   * describe una vuelta que en esta pantalla no ocurrió. */
  const textoCambioModo = esRegistro
    ? t("login.yaTengoCuenta", "¿Ya tienes cuenta? Entra")
    : esOlvide
    ? t("login.volverEntrar", "Volver a entrar")
    : t("login.registrate", "¿No tienes cuenta? Regístrate");

  return (
    <div className="portada">
      <PortadaBarra />

      <section className="portada-heroe">
        <PortadaArgumento />

        {/* `login-caja` a propósito: hereda los campos, los botones y
            los mensajes de error que ya estaban probados. Lo único
            nuevo es dónde se para dentro de la página. */}
        <form className="login-caja" onSubmit={enviar}>
          <p className="login-subtitulo">{titulo}</p>

          {aviso && <div className="mensaje-ok">{aviso}</div>}
          {error && <div className="mensaje-error" role="alert">{error}</div>}

          {esOlvide && (
            <p className="tenue texto-chico">
              {t("login.olvideExplica", "Escribe tu email y te mandamos un link para definir una contraseña nueva.")}
            </p>
          )}

          {esRegistro && (
            <label className="campo">
              <span>{t("login.nombre", "Nombre")}</span>
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder={t("login.comoTeLlamas", "Cómo te llamas")}
                autoComplete="name"
                disabled={ocupado}
              />
            </label>
          )}

          <label className="campo">
            <span>{t("login.email", "Email")}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              autoComplete="email"
              disabled={ocupado}
              required
            />
          </label>

          {!esOlvide && (
            <label className="campo">
              <span>{t("login.password", "Contraseña")}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={esRegistro ? t("login.minimo8", "Mínimo 8 caracteres") : "••••••••"}
                autoComplete={esRegistro ? "new-password" : "current-password"}
                disabled={ocupado}
                required
              />
            </label>
          )}

          <button className="boton-primario" type="submit" disabled={ocupado}>
            {textoBoton}
          </button>

          {modo === "entrar" && (
            <button
              className="boton-texto"
              type="button"
              onClick={() => cambiarModo("olvide")}
              disabled={ocupado}
            >
              {t("login.olvide", "Me olvidé la contraseña")}
            </button>
          )}

          <button
            className="boton-texto"
            type="button"
            onClick={() => cambiarModo(esRegistro || esOlvide ? "entrar" : "registrarse")}
            disabled={ocupado}
          >
            {textoCambioModo}
          </button>
        </form>
      </section>

      <PortadaPruebas />
      <PortadaPie />
    </div>
  );
}
